import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { hasRole } from '@conversia/shared-types';
import * as agentsApi from '@/api/agents.api';
import type { Cartera, FilaDeCartera } from '@/api/cartera.api';
import * as conversationsApi from '@/api/conversations.api';
import * as usersApi from '@/api/users.api';
import { useToast } from '@/hooks/useToast';
import { asignadoA, avisoAlAsignar } from '@/lib/cartera';
import { conAsignacion, type ResultadoDeLote } from '@/lib/tablero-clientes';
import { useAuthStore } from '@/store/auth.store';

const motivoDe = (e: unknown) => (e instanceof Error ? e.message : 'No se pudo asignar');

/**
 * «ASIGNAR» EL CHAT DE UN CLIENTE: a un agente de IA, a mí o a alguien del equipo (tanda 5).
 * En la tanda 6 salió de la pantalla de Clientes para que lo compartan la ventana de la ficha
 * y la tabla. El valor es el del desplegable: `agente:<id>`, `yo` o `persona:<id>`.
 *
 * Asignar se ve al instante en la lista de Clientes (la caché `['cartera']`) y, si el servidor
 * lo rechaza, vuelve atrás.
 *
 * `activo`: mientras sea false no se piden ni los agentes ni el equipo.
 */
export function useAsignacion({ activo = true }: { activo?: boolean } = {}) {
  const qc = useQueryClient();
  const toast = useToast();
  const yo = useAuthStore((s) => s.user);
  // GET /api/users es solo del dueño (OWNER); con otro rol, la opción no aparece.
  const veAlEquipo = yo ? hasRole(yo.role, 'OWNER') : false;

  const consultaDeAgentes = useQuery({
    queryKey: ['agents', 'activos'],
    queryFn: () => agentsApi.listAgents({ status: 'ACTIVE', take: 100 }),
    enabled: activo,
  });
  const equipo = useQuery({
    queryKey: ['users', 'activos'],
    queryFn: () => usersApi.listUsers({ take: 100 }),
    enabled: activo && veAlEquipo,
  });
  const usuarios = (equipo.data?.items ?? []).filter((u) => u.isActive);
  const agentes = (consultaDeAgentes.data?.items ?? []).map((a) => ({ id: a.id, name: a.name }));
  /** null: tu rol no puede ver al equipo, y la opción no se ofrece. */
  const personas = veAlEquipo ? usuarios.filter((u) => u.id !== yo?.id).map((u) => ({ id: u.id, nombre: u.name || u.email })) : null;

  /** Lo que cuesta asignar uno, sin avisos ni caché: lo usan la mutación y el lote. */
  const ejecutar = async ({ fila, valor }: { fila: FilaDeCartera; valor: string }): Promise<[string, string?]> => {
    const conv = fila.conversacion;
    if (!conv) throw new Error('Todavía no ha escrito: no hay chat que asignar.');
    if (valor.startsWith('agente:')) {
      await conversationsApi.assignAgent(conv.id, valor.slice('agente:'.length));
      // Si lo tenía una persona, vuelve a la IA, como «Devolver al asistente» del chat.
      if (conv.personaId) await conversationsApi.assignUser(conv.id, null);
      if (conv.estado === 'WAITING_HUMAN') await conversationsApi.updateStatus(conv.id, 'ACTIVE');
      return ['Listo: la IA atiende este chat.'];
    }
    if (valor === 'yo') {
      // La sesión puede no haber cargado todavía (revisión de Codex): mejor un aviso que un error suelto.
      if (!yo?.id) throw new Error('Todavía no sé quién eres: recarga la página e inténtalo de nuevo.');
      await conversationsApi.assignUser(conv.id, yo.id);
      return ['Lo atiendes tú.', 'La IA se aparta de este chat.'];
    }
    const personaId = valor.slice('persona:'.length);
    await conversationsApi.assignUser(conv.id, personaId);
    return [`Se lo pasaste a ${personas?.find((p) => p.id === personaId)?.nombre ?? 'tu equipo'}.`, 'La IA se aparta de este chat.'];
  };

  /** Cambia UNA fila de la lista de Clientes en la caché (si la lista está cargada). */
  const ponerEnLaLista = (contactId: string, cambio: (f: FilaDeCartera) => FilaDeCartera) =>
    qc.setQueryData<Cartera>(['cartera'], (c) => c && { ...c, filas: c.filas.map((f) => (f.contactId === contactId ? cambio(f) : f)) });

  const refrescar = (convId?: string) => {
    void qc.invalidateQueries({ queryKey: ['cartera'] });
    void qc.invalidateQueries({ queryKey: ['conversations'] });
    if (convId) void qc.invalidateQueries({ queryKey: ['conversation', convId] });
  };

  const mutacion = useMutation({
    mutationFn: ejecutar,
    onMutate: async ({ fila, valor }) => {
      await qc.cancelQueries({ queryKey: ['cartera'] });
      // Lo que la caché decía justo antes, no la fila del clic (que puede ser más vieja).
      const antes = qc.getQueryData<Cartera>(['cartera'])?.filas.find((f) => f.contactId === fila.contactId)?.conversacion;
      ponerEnLaLista(fila.contactId, (f) => conAsignacion(f, valor, yo?.id));
      return { antes };
    },
    onSuccess: ([titulo, detalle]) => toast.success(titulo, detalle),
    onError: (e, { fila }, contexto) => {
      // Se deshace SOLO la asignación (vive en `conversacion`): otra edición de esa fila que esté
      // en camino —la zona, el presupuesto— no se pisa con una copia vieja (revisión de Codex).
      if (contexto?.antes !== undefined) ponerEnLaLista(fila.contactId, (f) => ({ ...f, conversacion: contexto.antes ?? null }));
      toast.error(motivoDe(e));
    },
    // También si falló a medias (son hasta tres llamadas): la lista vuelve a decir la verdad.
    onSettled: (_r, _e, { fila }) => refrescar(fila.conversacion?.id),
  });

  return {
    agentes,
    personas,
    yoId: yo?.id ?? null,
    ocupado: mutacion.isPending,
    /** «Lo atiende la IA (Asistente)», «Lo atiendes tú»… o null si todavía no tiene chat. */
    asignadoDe: (fila: FilaDeCartera): string | null =>
      fila.conversacion
        ? asignadoA(fila.conversacion, { yoId: yo?.id, agentes, personas: usuarios.map((u) => ({ id: u.id, name: u.name, email: u.email })) })
        : null,
    asignar: (fila: FilaDeCartera, valor: string): void => {
      // M5: a un contacto personal el agente no le escribe; no se dice «Listo».
      const aviso = avisoAlAsignar(fila, valor);
      if (aviso) toast.warning(aviso);
      else mutacion.mutate({ fila, valor });
    },
    /**
     * Varios de una vez, uno detrás de otro y sin un aviso por cada uno: devuelve cuántos sí y
     * cuáles no y por qué (los que no tienen chat ni se intentan). El resumen lo da quien llama.
     * ponytail: N llamadas seguidas; una ruta en lote si se marcan cientos.
     */
    asignarVarios: async (filas: FilaDeCartera[], valor: string): Promise<ResultadoDeLote> => {
      const r: ResultadoDeLote = { hechas: 0, fallos: [] };
      await qc.cancelQueries({ queryKey: ['cartera'] });
      for (const fila of filas) {
        const motivo = !fila.conversacion ? 'Todavía no tiene chat.' : avisoAlAsignar(fila, valor) ? 'Es un contacto personal: el agente no le escribe.' : null;
        if (motivo) {
          r.fallos.push({ nombre: fila.nombre, motivo });
          continue;
        }
        ponerEnLaLista(fila.contactId, (f) => conAsignacion(f, valor, yo?.id));
        try {
          await ejecutar({ fila, valor });
          r.hechas += 1;
        } catch (e) {
          ponerEnLaLista(fila.contactId, () => fila);
          r.fallos.push({ nombre: fila.nombre, motivo: motivoDe(e) });
        }
      }
      refrescar();
      return r;
    },
  };
}
